import {IExportTemplate, IRuleset} from "../../../index";
import {collectUniqueRulesetsFromRoots} from "../../ruleset/copy-wizard/copyRulesetGraph";

export type TCopyPreviewKind = "synctemplate" | "ruleset";

export interface ICopyPreviewRow {
    kind: TCopyPreviewKind;
    template?: IExportTemplate;
    ruleset?: IRuleset;
}

export interface ISyncTemplateRulesetPlanOptions {
    /** When false, the template’s top-level ruleset and its sub-ruleset closure are omitted; export-flow (mapping) rulesets stay included. Default true. */
    includeTemplateMainRuleset?: boolean;
}

/**
 * Computes unique rulesets for the wizard: template-level ruleset (optional) plus every export-flow (mapping)
 * ruleset, expanded transitively via sub-ruleset references in `rulesetsById`.
 */
export function collectRulesetClosureForSyncTemplates(
    rootTemplates: IExportTemplate[],
    rulesetsById: Map<number, IRuleset>,
    options?: ISyncTemplateRulesetPlanOptions,
): IRuleset[] {
    const includeTemplateMain = options?.includeTemplateMainRuleset !== false;
    const directRoots: IRuleset[] = [];
    for (const tpl of rootTemplates) {
        if (includeTemplateMain && tpl.ruleset) {
            directRoots.push(tpl.ruleset);
        }
        for (const flow of tpl.exportflow || []) {
            if (flow.ruleset) {
                directRoots.push(flow.ruleset);
            }
        }
    }
    return collectUniqueRulesetsFromRoots(directRoots, rulesetsById);
}

export function buildOrderedPreviewRows(
    rootTemplates: IExportTemplate[],
    uniqueRulesets: IRuleset[],
    includeTemplateMainRuleset = true,
): ICopyPreviewRow[] {
    const rulesetsById = new Map(uniqueRulesets.map((r) => [r.id, r]));
    const shownRuleIds = new Set<number>();
    const rows: ICopyPreviewRow[] = [];
    for (const tpl of rootTemplates) {
        rows.push({kind: "synctemplate", template: tpl});
        const directRoots: IRuleset[] = [];
        if (includeTemplateMainRuleset && tpl.ruleset) {
            directRoots.push(tpl.ruleset);
        }
        for (const flow of tpl.exportflow || []) {
            if (flow.ruleset) {
                directRoots.push(flow.ruleset);
            }
        }
        const localClosure = collectUniqueRulesetsFromRoots(directRoots, rulesetsById);
        for (const rs of localClosure) {
            if (!shownRuleIds.has(rs.id)) {
                shownRuleIds.add(rs.id);
                rows.push({kind: "ruleset", ruleset: rs});
            }
        }
    }
    return rows;
}
